import React, { useEffect } from 'react';
import Icon from '../../shared/Icon';
import './AlertSteps.css';

/**
 * The live match count against current data, as the threshold is edited (AC7).
 *
 * Two distinctions the response draws that this surfaces rather than flattens:
 *
 *  - **`dataSnapshot`.** "current" means the historical period's data as it
 *    stands today, not as it stood then. A count shown beside a threshold is
 *    a statement about today's data and says so.
 *  - **`volumeGuard.checked` vs `configured`.** `checked: false` means no rule
 *    actually applied to this run — an alert can look guarded and not be, and
 *    a share rule that cannot apply to this comparison leaves it as exposed as
 *    one with no guard at all. That is worth surfacing, so it is.
 *
 * Warnings come back as written; a missing timezone is one of them.
 */
/**
 * The count could not be worked out — said to an author, not to whoever wrote
 * the API.
 *
 * **The detail is diagnostic, not instruction.** It used to be printed here
 * verbatim, so a validation failure put "stored comparisons must be a list, got
 * dict" under the comparison controls: true, unactionable, and naming an
 * internal representation the author has never seen. The problems the author
 * *can* fix already have their own rendering — `AlertSpecProblems` above, and
 * the classed refusal on the question — so anything reaching here is a fault on
 * our side, and the honest thing is to say so.
 *
 * Kept reachable rather than swallowed: the `title` puts it one hover away and
 * it still goes to the console, so a support conversation is not left guessing.
 *
 * It does not say "carry on and save". `Next` is not gated on this error, and
 * the write gate is deliberately lenient, so an author told to continue through
 * a failure whose cause was a malformed comparison would store exactly that.
 * What it does say is the thing that is actually unsafe to assume: an
 * unavailable count is not a count of zero. Raised in review of #177.
 */
const MatchCountError = ({ detail }) => {
  useEffect(() => {
    if (detail) console.warn('Alert live count failed:', detail);
  }, [detail]);

  return (
    <div className="resui-banner resui-banner--error alert-match-count" title={detail}>
      <Icon name="error_outline" />
      <span className="resui-banner-text">
        The live count could not be worked out. Try again — and read this as the
        count being unavailable, not as nothing matching.
      </span>
    </div>
  );
};

const AlertMatchCount = ({ result, error, loading, ready, notReadyReason }) => {
  if (!ready) {
    return (
      <div className="resui-banner resui-banner--info alert-match-count">
        <Icon name="functions" />
        <span className="resui-banner-text">
          {/* Named rather than generic: the count also stops when the guard is
              out of range, and blaming the dataset then sends the author to
              the wrong field. */}
          {notReadyReason
            || 'Select at least one dataset and complete the threshold to see how many subjects match.'}
        </span>
      </div>
    );
  }

  if (error) return <MatchCountError detail={error} />;

  if (loading && !result) {
    return (
      <div className="resui-banner resui-banner--info alert-match-count">
        <Icon name="hourglass_top" />
        <span className="resui-banner-text">Evaluating against current data…</span>
      </div>
    );
  }

  if (!result) return null;

  const guard = result.volumeGuard ?? {};
  const guardUnchecked = guard.configured && !guard.checked;

  return (
    <div className="alert-match-panel">
      <div className={`alert-match-headline${loading ? ' alert-match-stale' : ''}`}>
        <span className="alert-match-number">{result.subjectsMatched}</span>
        <span className="alert-match-of">
          of {result.subjectsEvaluated} subjects match
        </span>
        {loading && <span className="alert-match-updating">updating…</span>}
      </div>

      <p className="alert-match-meta">
        {result.comparisons} comparison{result.comparisons === 1 ? '' : 's'} across{' '}
        {result.tablesRead} table{result.tablesRead === 1 ? '' : 's'}, anchored{' '}
        {result.anchorDate}. Data snapshot: {result.dataSnapshot}.
      </p>

      {guard.explanation && (
        <p className="alert-match-meta">{guard.explanation}</p>
      )}

      {guard.wouldTrip && (
        <div className="resui-banner resui-banner--alert alert-match-count">
          <Icon name="warning_amber" />
          <span className="resui-banner-text">
            The volume guard would trip on this run
            {guard.reasons?.length > 0 ? ` (${guard.reasons.join(', ')})` : ''}.
          </span>
        </div>
      )}

      {guardUnchecked && (
        <div className="resui-banner resui-banner--alert alert-match-count">
          <Icon name="shield" />
          <span className="resui-banner-text">
            A guard is configured but no rule applied to this run
            {guard.shareSkipped ? ` (${guard.shareSkipped})` : ''}. This alert is
            as exposed as one with no guard at all.
          </span>
        </div>
      )}

      {/* Keyed by index as well as text: the API may legitimately repeat the
          same warning per comparison or per dataset, and duplicate keys break
          reconciliation. */}
      {(result.warnings ?? []).map((warning, i) => (
        <div key={`${i}-${warning}`} className="resui-banner resui-banner--alert alert-match-count">
          <Icon name="warning_amber" />
          <span className="resui-banner-text">{warning}</span>
        </div>
      ))}
    </div>
  );
};

export default AlertMatchCount;
